import Point from "../models/Point";
import {
  getDrawingAreaElement,
  eventPointToDrawingArea,
  setLeftTopInStyle,
  getLeftTopFromStyle,
  getCenterOffsetFromStyle,
  getScale,
} from "./GraficElementUtils";
import Logger from "../Logger";
import type Room from "../models/Room";
import type Page from "../models/Page";
import { noItx } from "../translationItx/itx";

const logger = new Logger();
var dxy = { x: 0, y: 0 };

export class MoveHandler {
  static dragElement: any = null;

  elm: any = null;
  elmSub: any = null;
  thisElement: any;
  parent: any;
  subElement: any;

  onMouseDown: any;
  onMouseUp: any;

  onClick: any;
  onDblClick: any;
  onMoveFinished: any;
  onMove: any;
  onMoveInfo: any;
  onCancel: any;
  onMoveStarted: any;
  onMoveStartedCalled = false;
  isSubelement: boolean = false;
  mouseIsDown: boolean = false;
  room: Room | undefined;
  mainPage: Page;

  pStart = new Point(0, 0);
  pStartSceen = new Point(0, 0);
  pLastMove: Point | undefined;

  currentPointerId: number | null = null;

  constructor(
    thisElement: React.RefObject<HTMLDivElement>,
    subElement: string | null,
    room: Room | undefined,
    mainPage: Page,
  ) {
    this.thisElement = thisElement;
    this.subElement = subElement;
    this.elm = thisElement.current;
    this.room = room;
    this.mainPage = mainPage;

    if (this.elm) {
      this.parent = getDrawingAreaElement(this.elm);

      let xElm = this.elm as HTMLElement;
      if (subElement) {
        this.elmSub = thisElement.current?.querySelector(subElement);
        if (this.elmSub) {
          xElm = this.elmSub as HTMLElement;
        } else {
          logger.devError("no subElement found: ", subElement);
        }
      }

      xElm.addEventListener("pointerdown", this.onPointerdown);
      xElm.addEventListener("pointermove", this.onPointerMove);
      xElm.addEventListener("pointerup", this.onPointerup);
      xElm.addEventListener("pointercancel", this.onPointerCancel);
      xElm.addEventListener("click", this.onClickHandler);
      xElm.addEventListener("dblclick", this.onDoubleClick);
    }
  }

  release = () => {
    let xElm = this.elm as HTMLElement;
    if (this.subElement) {
      if (this.elmSub) {
        xElm = this.elmSub as HTMLElement;
      } else {
        logger.devError("no subElement found during release: ", this.subElement);
      }
    }

    if (xElm) {
      xElm.removeEventListener("pointerdown", this.onPointerdown);
      xElm.removeEventListener("pointermove", this.onPointerMove);
      xElm.removeEventListener("pointerup", this.onPointerup);
      xElm.removeEventListener("pointercancel", this.onPointerCancel);
      xElm.removeEventListener("click", this.onClickHandler);
      xElm.removeEventListener("dblclick", this.onDoubleClick);
    }
  };

  getCenter = (): Point => {
    if (this.subElement) {
      const r1 = this.thisElement.current.getBoundingClientRect();
      const r2 = this.elm.getBoundingClientRect();
      const p = new Point(r2.left - r1.left + r2.width / 2, r2.top - r1.top + r2.height / 2);
      const scale = getScale(this.elm);
      p.x /= scale;
      p.y /= scale;

      return p;
    } else {
      return getCenterOffsetFromStyle(MoveHandler.dragElement);
    }
  };

  onPointerMove = (event: PointerEvent) => {
    // Only handle the pointer that started the drag
    if (this.currentPointerId && event.pointerId !== this.currentPointerId) {
      return;
    }

    if (!this.mouseIsDown) {
      return;
    }

    event.stopPropagation();

    if (MoveHandler.dragElement) {
      let pLocal = eventPointToDrawingArea(event);
      this.pLastMove = pLocal;

      console.log("Pointer moved:", pLocal);

      let delta = 0;

      const p = {
        x: event.clientX,
        y: event.clientY,
      };

      const dx = p.x - this.pStartSceen.x;
      const dy = p.y - this.pStartSceen.y;
      delta = Math.sqrt(dx * dx + dy * dy);

      if (pLocal && this.room?.roomtoFloorDrawingTransform) {
        pLocal = this.room.roomtoFloorDrawingTransform.transformPointInvers(pLocal);
      }

      if (pLocal) {
        if (!this.onMoveStartedCalled && delta > 10) {
          this.onMoveStartedCalled = true;
          this.onMoveStarted && this.onMoveStarted(pLocal);
        }

        if (!this.onMoveStartedCalled) {
          // MoveHandler.dragElement.style.cursor = "pointer";
        } else {
          // MoveHandler.dragElement.style.cursor = undefined;

          if (this.onMove) {
            this.onMove(pLocal);
          } else {
            // drag the element to mooved position -> here have to look if it is in page?
            const center = this.getCenter();

            const elms = document.elementsFromPoint(p.x, p.y);

            // avoid mouse move in PageHeder, Scenes, Actors
            if (elms) {
              for (let i = 0; i < elms.length; i++) {
                const elm = elms[i];
                const header = elm?.closest("#pageHeader, .sceneGroups, .actorArea"); // no itx
                if (header) {
                  return;
                }
              }
            }

            var p1 = pLocal;
            if (center.x) {
              p1 = new Point(pLocal.x - center.x - dxy.x, pLocal.y - center.y - dxy.y);
            } else {
              p1 = new Point(pLocal.x - dxy.x, pLocal.y - dxy.y);
            }

            setLeftTopInStyle(MoveHandler.dragElement, p1);

            if (this.onMoveInfo) {
              this.onMoveInfo(pLocal);
            }
          }
        }
      }
    } else {
      logger.devError("no dragElement");
    }
  };

  onClickHandler = (event: MouseEvent) => {
    event.stopPropagation();
    console.log("onClick");
    if (this.onClick) {
      this.onClick(event);
    }
  };

  onDoubleClick = (event) => {
    event.stopPropagation();
    console.log("onDoubleClick");
    if (this.onDblClick) {
      this.onDblClick(event);
    }
  };

  onPointerdown = (event: PointerEvent) => {
    this.mouseIsDown = true;

    this.pStart = eventPointToDrawingArea(event) || new Point(0, 0);
    this.pStartSceen = new Point(event.clientX, event.clientY);

    // logger.log("pointer down", this.pStart);

    if (event.target && (event.target as Element).getAttribute(noItx("contenteditable"))) {
      logger.log("target contenteditable ");
      return;
    }

    // Prevent default to avoid conflicts with legacy events
    event.stopPropagation();

    MoveHandler.dragElement = this.thisElement.current;

    // Pointer capturing - this is the key feature we wanted
    if (this.elmSub) {
      this.elmSub.setPointerCapture(event.pointerId);
    } else if (this.elm) {
      this.elm.setPointerCapture(event.pointerId);
    }

    let pMouseDownInDrawingSpace = eventPointToDrawingArea(event);
    if (pMouseDownInDrawingSpace) {
      if (this.room?.roomtoFloorDrawingTransform) {
        pMouseDownInDrawingSpace = this.room.roomtoFloorDrawingTransform.transformPointInvers(pMouseDownInDrawingSpace);
      }

      let leftTop = getLeftTopFromStyle(MoveHandler.dragElement);
      if (isNaN(leftTop.x)) {
        const r = MoveHandler.dragElement.getBoundingClientRect();
        leftTop = new Point(r.left, r.top);
      }
      const center = this.getCenter();

      if (center.x) {
        dxy = {
          x: pMouseDownInDrawingSpace.x - leftTop.x - center.x,
          y: pMouseDownInDrawingSpace.y - leftTop.y - center.y,
        };
      } else {
        dxy = {
          x: pMouseDownInDrawingSpace.x - leftTop.x,
          y: pMouseDownInDrawingSpace.y - leftTop.y,
        };
      }

      // Store pointerId for later use
      this.currentPointerId = event.pointerId;

      this.onMoveStartedCalled = false;
      if (this.onMouseDown) {
        this.onMouseDown(pMouseDownInDrawingSpace);
      }
    }
  };

  onPointerCancel = (event: PointerEvent) => {
    // Only handle the pointer that started the drag
    if (this.currentPointerId && event.pointerId !== this.currentPointerId) {
      return;
    }

    this.mouseIsDown = false;

    // Release pointer capture
    const pointerId = this.currentPointerId || event.pointerId;
    if (this.elmSub) {
      try {
        this.elmSub.releasePointerCapture(pointerId);
      } catch (e) {
        // Ignore errors if pointer was already released
      }
    } else if (this.elm) {
      try {
        this.elm.releasePointerCapture(pointerId);
      } catch (e) {
        // Ignore errors if pointer was already released
      }
    }

    // Clear stored pointerId
    this.currentPointerId = null;

    MoveHandler.dragElement = null;

    if (this.onCancel) {
      this.onCancel();
    }
  };

  onPointerup = (event: PointerEvent) => {
    event.stopPropagation();

    // Only handle the pointer that started the drag
    if (this.currentPointerId && event.pointerId !== this.currentPointerId) {
      return;
    }

    if (!this.mouseIsDown) {
      return;
    }

    this.mouseIsDown = false;

    if (MoveHandler.dragElement) {
      // MoveHandler.dragElement.style.cursor = null;

      if (this.onMouseUp) {
        this.onMouseUp(event);
      }
      if (this.onMoveFinished) {
        this.onMoveFinished(getLeftTopFromStyle(MoveHandler.dragElement), this.pLastMove); // p in in MainPage Coordinates
      }
    }

    // Release pointer capture
    const pointerId = this.currentPointerId || event.pointerId;
    if (this.elmSub) {
      try {
        this.elmSub.releasePointerCapture(pointerId);
      } catch (e) {
        // Ignore errors if pointer was already released
      }
    } else if (this.elm) {
      try {
        this.elm.releasePointerCapture(pointerId);
      } catch (e) {
        // Ignore errors if pointer was already released
      }
    }

    // Clear stored pointerId
    this.currentPointerId = null;

    MoveHandler.dragElement = null;
  };
}
